import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UserProfile from '../auth/UserProfile';
import V2ThemePicker from '../v2/V2ThemePicker';
import { GroupSelector } from '../timezone-groups/GroupSelector';

interface HeaderProps {
  onToggleSidebar: () => void;
  onOpenCommandPalette: () => void;
  onCalendarChange?: () => void;
  onLoadGroup?: (groupId: string) => void;
  hasChanges?: boolean;
}

export function Header({ onToggleSidebar, onOpenCommandPalette, onCalendarChange, onLoadGroup, hasChanges = false }: HeaderProps) {
  const { t } = useTranslation();

  // V2-only theme toggle: flip between Forest (the dark v2 palette) and the
  // last light v2 palette the picker had selected. No more legacy dark-mode.
  const [v2Theme, setV2Theme] = useState<string>(
    () => (typeof window !== 'undefined' && localStorage.getItem('v2Theme')) || 'sky-linen'
  );
  useEffect(() => {
    const sync = () => setV2Theme(localStorage.getItem('v2Theme') || 'sky-linen');
    window.addEventListener('wv-v2-theme-change', sync);
    return () => window.removeEventListener('wv-v2-theme-change', sync);
  }, []);
  const isDark = v2Theme === 'forest';
  const toggleV2Theme = () => {
    const cur = localStorage.getItem('v2Theme') || 'sky-linen';
    let next: string;
    if (cur === 'forest') {
      const last = localStorage.getItem('v2LastLightTheme');
      next = (last && last !== 'forest') ? last : 'sky-linen';
    } else {
      localStorage.setItem('v2LastLightTheme', cur);
      next = 'forest';
    }
    localStorage.setItem('v2Theme', next);
    window.dispatchEvent(new Event('wv-v2-theme-change'));
  };

  return (
    <header className="h-12 border-b border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm sticky top-0 z-40">
      <div className="flex items-center justify-between px-4 h-full">
        {/* Left side - Logo with sidebar toggle */}
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleSidebar}
            className="zw-brand-btn flex items-center gap-2 p-1 rounded-md transition-colors"
          >
            <div className="zw-logo-monogram" aria-hidden="true">ZW</div>
            <div className="zw-logo-wordmark text-gray-900 dark:text-white">
              ZoneWise
            </div>
          </button>
        </div>
        
        {/* Center - Search */}
        <div className="flex-1 max-w-lg mx-4">
          <button
            onClick={onOpenCommandPalette}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span>{t('header.searchPlaceholder')}</span>
            <div className="ml-auto flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">⌘</kbd>
              <kbd className="px-1.5 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">K</kbd>
            </div>
          </button>
        </div>
        
        {/* Right side - Group selector, theme toggle and user profile */}
        <div className="flex items-center gap-3">
          <GroupSelector onLoadGroup={onLoadGroup} hasChanges={hasChanges} />
          
          <V2ThemePicker />

          <button
            onClick={toggleV2Theme}
            className="header-theme-toggle p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
            title={isDark ? t('header.switchToLight') : t('header.switchToForest')}
            aria-label={isDark ? t('header.switchToLight') : t('header.switchToForest')}
          >
            {!isDark ? (
              // Moon icon — currently on a light v2 palette, click to go Forest
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            ) : (
              // Sun icon — currently on Forest, click to go back to last light palette
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            )}
          </button>
          
          <div className="header-user-profile">
            <UserProfile />
          </div>
        </div>
      </div>
    </header>
  );
}